'use client';

import { useEffect, useRef, useState } from 'react';

import {
  PRELOADER_COMPLETE_DURATION_MS,
  PRELOADER_PROGRESS_CAP,
  PRELOADER_Z_INDEX,
} from '@/constants/preloader';
import { whenBootReady } from '@/lib/scrollRestoration';
import { durations } from '@/motion/tokens';

import styles from './Preloader.module.css';

type PreloaderProps = {
  loadingLabel: string;
};

function whenDocumentLoaded(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (document.readyState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true });
  });
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function setFillProgress(el: HTMLElement, progress: number): void {
  el.style.transform = `scaleX(${progress})`;
}

function prefersReducedMotionNow(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Read the painted scaleX, then kill the CSS crawl without snapping back to the
 * stylesheet's `scaleX(0)`. `Animation.commitStyles()` + `cancel()` can briefly
 * revert to that base style and reads as a backwards jump.
 */
function freezeCrawlProgress(el: HTMLElement): number {
  const { transform } = getComputedStyle(el);
  let scaleX = PRELOADER_PROGRESS_CAP;

  if (transform && transform !== 'none') {
    scaleX = new DOMMatrixReadOnly(transform).m11;
  }

  // Inline transform first, then disable the CSS animation in the same turn
  // so layout never paints the stylesheet's scaleX(0).
  el.style.transform = `scaleX(${scaleX})`;
  el.style.animation = 'none';

  for (const animation of el.getAnimations()) {
    animation.cancel();
  }

  return scaleX;
}

/**
 * Full-viewport brand preloader. CSS crawls the masked bird mark fill from
 * first paint to 95%; once the document has loaded and scroll restoration has
 * run, JS eases to 100% and fades the overlay out.
 */
export function Preloader({ loadingLabel }: PreloaderProps) {
  const fillRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(true);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const fill = fillRef.current;
    if (!fill) return;

    let cancelled = false;
    let completeAnimation: Animation | null = null;
    const reducedMotion = prefersReducedMotionNow();

    void (async () => {
      await Promise.all([whenDocumentLoaded(), whenBootReady()]);
      if (cancelled) return;

      if (reducedMotion) {
        freezeCrawlProgress(fill);
        setFillProgress(fill, 1);
      } else {
        const fromScaleX = freezeCrawlProgress(fill);

        completeAnimation = fill.animate(
          [
            { transform: `scaleX(${fromScaleX})` },
            { transform: 'scaleX(1)' },
          ],
          {
            duration: PRELOADER_COMPLETE_DURATION_MS,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'forwards',
          },
        );

        try {
          await completeAnimation.finished;
        } catch {
          // Aborted on cleanup — exit quietly.
        }
        if (cancelled) return;

        completeAnimation.commitStyles();
        completeAnimation.cancel();
        setFillProgress(fill, 1);
      }

      setExiting(true);
      await wait(Math.round(durations.base * 1000));
      if (cancelled) return;

      setVisible(false);
    })();

    return () => {
      cancelled = true;
      completeAnimation?.cancel();
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={styles.root}
      data-exiting={exiting ? '' : undefined}
      role="status"
      aria-live="polite"
      aria-busy={!exiting}
      style={{ zIndex: PRELOADER_Z_INDEX }}
    >
      <span className={styles.visuallyHidden}>{loadingLabel}</span>
      <div className={styles.logo} aria-hidden="true">
        <div ref={fillRef} className={styles.fill} />
      </div>
    </div>
  );
}
